// Sistema de overlays: alert, modal genérico y modal de lote especializado.
//
// API basada en promesas — la vista hace `await openLoteModal(...)` y recibe el
// lote elegido (o null si canceló). Esto evita callbacks anidadas y mantiene
// el flujo del scanner (que ya es async) lineal y legible.
//
// Hay UN solo nodo overlay reutilizable, montado al body. Cada apertura limpia
// el contenido anterior — no soportamos overlays apilados a propósito (la app
// nunca los necesita y simplifica el cleanup).

import { icon } from './icons.js';

let _overlay = null;

function _ensure() {
  if (_overlay) return _overlay;
  _overlay = document.createElement('div');
  _overlay.id = 'overlay-host';
  document.body.appendChild(_overlay);
  return _overlay;
}

function _close() {
  if (_overlay) _overlay.innerHTML = '';
}

/** Escapa HTML para inyectar texto de usuario sin XSS. */
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// ---------------------------------------------------------------------------
// Alert fullscreen (error/warn) — bloqueante, devuelve cuando el user cierra.
// ---------------------------------------------------------------------------

export function openAlert({ tipo = 'ok', titulo, detalle, accion = 'Entendido', accion2, accion2Onclick }) {
  return new Promise((resolve) => {
    const host = _ensure();
    if (tipo === 'error' || tipo === 'warn') {
      const cls = tipo === 'error' ? '--alert-error' : '--alert-warn';
      const iconHtml = tipo === 'error'
        ? icon('octagon-x', { size: 72 })
        : icon('alert-triangle', { size: 72 });
      const cta2html = accion2
        ? `<button class="alert-cta-secondary" id="ovl-action2" type="button">${escapeHtml(accion2)}</button>`
        : '';
      host.innerHTML = `
        <div class="scrim ${cls} fade-in" role="alert">
          <div class="modal">
            <div class="alert-fs-icon">${iconHtml}</div>
            <h2>${escapeHtml(titulo)}</h2>
            ${detalle ? `<p>${escapeHtml(detalle)}</p>` : ''}
            <div class="actions">
              <button class="btn --primary --block --xl" id="ovl-action">${escapeHtml(accion)}</button>
            </div>
            ${cta2html}
          </div>
        </div>`;
      host.querySelector('#ovl-action').addEventListener('click', () => { _close(); resolve('primary'); });
      if (accion2) {
        host.querySelector('#ovl-action2').addEventListener('click', () => {
          _close();
          if (accion2Onclick) try { accion2Onclick(); } catch (e) { console.error(e); }
          resolve('secondary');
        });
      }
      return;
    }
    // ok / info → banner pequeño que también se cierra con tap.
    host.innerHTML = `
      <div class="scrim --alert fade-in" role="alert">
        <div class="alert --${escapeHtml(tipo)}">
          <div class="ico">${tipo === 'ok' ? icon('check', { size: 22 }) : icon('alert-triangle', { size: 22 })}</div>
          <div class="body">
            <strong>${escapeHtml(titulo)}</strong>
            ${detalle ? `<span>${escapeHtml(detalle)}</span>` : ''}
          </div>
          <button class="dismiss" aria-label="Cerrar">×</button>
        </div>
      </div>`;
    host.querySelector('.dismiss').addEventListener('click', () => { _close(); resolve('primary'); });
    host.querySelector('.scrim').addEventListener('click', (e) => {
      if (e.target.classList.contains('scrim')) { _close(); resolve('primary'); }
    });
  });
}

// ---------------------------------------------------------------------------
// Modal "Lote del producto" — devuelve el lote (string) o null si cancela.
// ---------------------------------------------------------------------------

export function openLoteModal({ producto, loteSugerido = null }) {
  return new Promise((resolve) => {
    const host = _ensure();
    const ref = escapeHtml(producto.barcode || `#${producto.producto_id}`);
    const desc = escapeHtml(producto.descripcion);

    // Si hay lote sugerido → vista "lote-found" + link para editar manualmente.
    // Si no → vista "lote-missing" con input vacío.
    const renderFound = (sugerido) => `
      <h2>Lote del producto</h2>
      <p>${desc} <span class="hint">(${ref})</span></p>
      <div class="lote-found">
        <div class="ico">${icon('check', { size: 22 })}</div>
        <div class="info">
          <div class="label">Lote del producto</div>
          <div class="value">${escapeHtml(sugerido)}</div>
        </div>
      </div>
      <div class="actions">
        <button class="btn --ghost" id="lote-cancel">Cancelar</button>
        <button class="btn --primary" id="lote-confirm">Confirmar</button>
      </div>
      <button class="lote-edit-link" id="lote-edit">El lote real es distinto → introducir manualmente</button>`;

    const renderManual = (initial = '') => `
      <h2>Lote del producto</h2>
      <p>${desc} <span class="hint">(${ref})</span></p>
      ${loteSugerido ? '' : `
        <div class="lote-missing">
          <span style="display:inline-flex;">${icon('alert-triangle', { size: 18 })}</span>
          <span>Este producto no tiene un lote registrado. Introdúcelo manualmente.</span>
        </div>`}
      <div class="field">
        <label for="lote-input">Lote</label>
        <input id="lote-input" inputmode="text" autocomplete="off" placeholder="Ej: L-2026-0142" value="${escapeHtml(initial)}">
      </div>
      <p class="hint">Se memoriza para esta caja: si vuelves a escanear el mismo producto no se te volverá a preguntar.</p>
      <div class="actions">
        <button class="btn --ghost" id="lote-cancel">Cancelar</button>
        <button class="btn --primary" id="lote-confirm">Confirmar</button>
      </div>`;

    const open = (html) => {
      host.innerHTML = `<div class="scrim fade-in"><div class="modal">${html}</div></div>`;

      host.querySelector('#lote-cancel').addEventListener('click', () => { _close(); resolve(null); });

      const editBtn = host.querySelector('#lote-edit');
      if (editBtn) {
        editBtn.addEventListener('click', () => open(renderManual(loteSugerido || '')));
      }

      const confirm = host.querySelector('#lote-confirm');
      const input = host.querySelector('#lote-input');
      if (input) {
        // Auto-focus + Enter para confirmar
        setTimeout(() => input.focus(), 50);
        input.addEventListener('keydown', (e) => { if (e.key === 'Enter') confirm.click(); });
      }
      confirm.addEventListener('click', () => {
        const value = input ? input.value.trim() : (loteSugerido || '');
        if (!value) { input?.focus(); return; }
        _close();
        resolve(value);
      });
    };

    open(loteSugerido ? renderFound(loteSugerido) : renderManual());
  });
}

// ---------------------------------------------------------------------------
// Modal genérico — para "caja cerrada OK" y "impresora KO + PDF".
// ---------------------------------------------------------------------------

export function openCajaOkModal({ numero, sscc, palet = null }) {
  // `palet` opcional: { numero, totalCajasAhora }. Si viene, mostramos
  // explícitamente la asignación al palet para que el operario tenga
  // feedback visual de la jerarquía.
  return new Promise((resolve) => {
    const host = _ensure();
    const paletLine = palet
      ? `<div style="
            background: var(--c-accent);
            color: var(--c-accent-fg);
            padding: 0.6rem 0.85rem;
            border-radius: var(--radius);
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          ">
            <span style="display:inline-flex;">${icon('layers', { size: 20 })}</span>
            <span>Asignada al <strong>Palet #${palet.numero}</strong>
            (${palet.totalCajasAhora} ${palet.totalCajasAhora === 1 ? 'caja' : 'cajas'} en este palet)</span>
          </div>`
      : '';
    host.innerHTML = `
      <div class="scrim fade-in"><div class="modal">
        <h2>${icon('check-circle', { size: 24 })} Caja #${numero} cerrada</h2>
        <p>Etiqueta enviada a la impresora.</p>
        <div class="code">SSCC ${escapeHtml(sscc)}</div>
        ${paletLine}
        <p class="hint">Pega la etiqueta en la caja antes de continuar con la siguiente.</p>
        <div class="actions">
          <button class="btn --primary --block" id="caja-ok-continue">Empezar siguiente caja</button>
        </div>
      </div></div>`;
    host.querySelector('#caja-ok-continue').addEventListener('click', () => { _close(); resolve(); });
  });
}

export function openPaletOkModal({ numero, sscc }) {
  return new Promise((resolve) => {
    const host = _ensure();
    host.innerHTML = `
      <div class="scrim fade-in"><div class="modal">
        <h2>${icon('layers', { size: 24 })} Palet #${numero} creado</h2>
        <p>Etiqueta enviada a la impresora.</p>
        <div class="code">SSCC ${escapeHtml(sscc)}</div>
        <p class="hint">Pega la etiqueta en el palet. Las próximas cajas que cierres se asignarán a este palet hasta que crees otro o pulses "Cerrar palet".</p>
        <div class="actions">
          <button class="btn --primary --block" id="palet-ok-continue">Empezar a cargar el palet</button>
        </div>
      </div></div>`;
    host.querySelector('#palet-ok-continue').addEventListener('click', () => { _close(); resolve(); });
  });
}

export function openPaletPdfModal({ numero, sscc, path }) {
  return new Promise((resolve) => {
    const host = _ensure();
    host.innerHTML = `
      <div class="scrim fade-in"><div class="modal">
        <h2>${icon('printer', { size: 24 })} Impresora no disponible</h2>
        <p>Palet #${numero} creado. La etiqueta se guardó en red:</p>
        <div class="code">${escapeHtml(path)}</div>
        <p class="hint">SSCC ${escapeHtml(sscc)} — imprime manualmente. El palet ya está activo.</p>
        <div class="actions">
          <button class="btn --ghost" id="palet-pdf-copy">Copiar ruta</button>
          <button class="btn --primary" id="palet-pdf-continue">Continuar</button>
        </div>
      </div></div>`;
    host.querySelector('#palet-pdf-copy').addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(path); } catch {}
    });
    host.querySelector('#palet-pdf-continue').addEventListener('click', () => { _close(); resolve(); });
  });
}

export function openCajaPdfModal({ numero, sscc, path, palet = null }) {
  return new Promise((resolve) => {
    const host = _ensure();
    const paletLine = palet
      ? `<div style="
            background: var(--c-accent);
            color: var(--c-accent-fg);
            padding: 0.6rem 0.85rem;
            border-radius: var(--radius);
            font-weight: 600;
          "><span style="display:inline-flex;vertical-align:middle;">${icon('layers', { size: 20 })}</span> Asignada al <strong>Palet #${palet.numero}</strong>
            (${palet.totalCajasAhora} ${palet.totalCajasAhora === 1 ? 'caja' : 'cajas'} en este palet)
          </div>`
      : '';
    host.innerHTML = `
      <div class="scrim fade-in"><div class="modal">
        <h2>${icon('printer', { size: 24 })} Impresora no disponible</h2>
        <p>Caja #${numero} cerrada. La etiqueta se guardó en red:</p>
        <div class="code">${escapeHtml(path)}</div>
        ${paletLine}
        <p class="hint">SSCC ${escapeHtml(sscc)} — imprime manualmente desde un PC con acceso a esa ruta.</p>
        <div class="actions">
          <button class="btn --ghost" id="caja-pdf-copy">Copiar ruta</button>
          <button class="btn --primary" id="caja-pdf-continue">Continuar</button>
        </div>
      </div></div>`;
    host.querySelector('#caja-pdf-copy').addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(path); } catch {}
    });
    host.querySelector('#caja-pdf-continue').addEventListener('click', () => { _close(); resolve(); });
  });
}

/**
 * Resuelve a uno de:
 *   - 'seguir'       → cancelar, sigue empacando
 *   - 'parcial'      → enviar lo empacado como parcial; el pedido sigue abierto
 *   - 'definitivo'   → cerrar el pedido aunque falten unidades
 */
export function openIncompletoModal() {
  return new Promise((resolve) => {
    const host = _ensure();
    host.innerHTML = `
      <div class="scrim fade-in"><div class="modal">
        <h2>${icon('alert-triangle', { size: 24 })} Pedido incompleto</h2>
        <p>Hay líneas sin completar. ¿Qué quieres hacer?</p>
        <div style="display:flex;flex-direction:column;gap:0.5rem;">
          <button class="btn --primary --block --lg" id="inc-parcial" style="display:inline-flex;align-items:center;justify-content:center;gap:0.4rem;">
            ${icon('package', { size: 22 })} Enviar parcial
          </button>
          <p class="hint" style="margin:-0.25rem 0 0;">Sale lo empacado como un envío parcial; el pedido sigue abierto para más envíos.</p>
          <button class="btn --danger --block" id="inc-definitivo">Cerrar definitivamente</button>
          <p class="hint" style="margin:-0.25rem 0 0;">Lo que falte se anota como no enviado. No habrá más envíos del pedido.</p>
        </div>
        <div class="actions" style="margin-top:0.4rem;">
          <button class="btn --ghost --block" id="inc-cancel">Cancelar — seguir empacando</button>
        </div>
      </div></div>`;
    host.querySelector('#inc-cancel').addEventListener('click', () => { _close(); resolve('seguir'); });
    host.querySelector('#inc-parcial').addEventListener('click', () => { _close(); resolve('parcial'); });
    host.querySelector('#inc-definitivo').addEventListener('click', () => { _close(); resolve('definitivo'); });
  });
}

export function openPedidoCerradoLightbox({ pedidoRef, cajas, peso }) {
  return new Promise((resolve) => {
    const host = _ensure();
    const hora = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const pesoStr = peso != null ? `${peso.toFixed(1)} kg · ` : '';
    host.innerHTML = `
      <div class="scrim --success fade-in"><div class="modal">
        <div class="success-icon">${icon('check', { size: 80 })}</div>
        <h2>Pedido cerrado</h2>
        <p>${escapeHtml(pedidoRef)}</p>
        <p class="meta">${cajas} ${cajas === 1 ? 'caja' : 'cajas'} · ${pesoStr}${hora}</p>
        <div class="actions">
          <button class="btn --primary --block --xl" id="cerrado-volver">Volver a pedidos</button>
        </div>
      </div></div>`;
    host.querySelector('#cerrado-volver').addEventListener('click', () => { _close(); resolve(); });
  });
}

export function closeOverlay() { _close(); }
